import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { medicalRecordsApi } from '@/api';

export function useDiagnosesByMedicalRecord(medicalRecordId: string) {
  return useQuery({
    queryKey: ['medical-records', 'record', medicalRecordId, 'diagnoses'] as const,
    queryFn: () => medicalRecordsApi.getDiagnosesByMedicalRecord(medicalRecordId),
    enabled: !!medicalRecordId,
    staleTime: 1000 * 60 * 2,
  });
}

export function usePrescriptionsByMedicalRecord(medicalRecordId: string) {
  return useQuery({
    queryKey: ['medical-records', 'record', medicalRecordId, 'prescriptions'] as const,
    queryFn: () => medicalRecordsApi.getPrescriptionsByMedicalRecord(medicalRecordId),
    enabled: !!medicalRecordId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useExamRequestsByMedicalRecord(medicalRecordId: string) {
  return useQuery({
    queryKey: ['medical-records', 'record', medicalRecordId, 'exam-requests'] as const,
    queryFn: () => medicalRecordsApi.getExamRequestsByMedicalRecord(medicalRecordId),
    enabled: !!medicalRecordId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useClinicalEvolutionsByMedicalRecord(medicalRecordId: string) {
  return useQuery({
    queryKey: ['medical-records', 'record', medicalRecordId, 'clinical-evolutions'] as const,
    queryFn: () => medicalRecordsApi.getClinicalEvolutionsByMedicalRecord(medicalRecordId),
    enabled: !!medicalRecordId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCertificatesByMedicalRecord(medicalRecordId: string) {
  return useQuery({
    queryKey: ['medical-records', 'record', medicalRecordId, 'certificates'] as const,
    queryFn: () => medicalRecordsApi.getCertificatesByMedicalRecord(medicalRecordId),
    enabled: !!medicalRecordId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useMedicalRecordAudit(medicalRecordId: string) {
  return useQuery({
    queryKey: ['medical-records', 'record', medicalRecordId, 'audit'] as const,
    queryFn: () => medicalRecordsApi.getMedicalRecordAudit(medicalRecordId),
    enabled: !!medicalRecordId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useMedicalRecordTimeline(medicalRecordId: string) {
  return useQuery({
    queryKey: ['medical-records', 'record', medicalRecordId, 'timeline'] as const,
    queryFn: () => medicalRecordsApi.getMedicalRecordTimeline(medicalRecordId),
    enabled: !!medicalRecordId,
    staleTime: 1000 * 60 * 1,
  });
}

export function useUpdateDiagnosis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ diagnosisId, data }: { diagnosisId: string; data: { description?: string; cidCode?: string; type?: string; isActive?: boolean } }) =>
      medicalRecordsApi.updateDiagnosis(diagnosisId, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['medical-records'] }); },
  });
}

export function useDeleteDiagnosis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (diagnosisId: string) => medicalRecordsApi.deleteDiagnosis(diagnosisId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['medical-records'] }); },
  });
}

export function useUpdateMedicalRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, data }: { recordId: string; data: { status?: string } }) =>
      medicalRecordsApi.updateMedicalRecord(recordId, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['medical-records'] }); },
  });
}

export function useCloseMedicalRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, closedBy }: { recordId: string; closedBy: string }) =>
      medicalRecordsApi.closeMedicalRecord(recordId, closedBy),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['medical-records'] }); },
  });
}
